// src/pages/Tutorial.js
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { tutorAPI, homeworkAPI } from '../services/api';
import ReactMarkdown from 'react-markdown'; // +++ NEW IMPORT +++

const LineChart = ({ data }) => {
    const width = 600;
    const height = 200;
    const padding = 40;

    const pathData = data.map((point, index) => {
        const x = padding + (index / (data.length > 1 ? data.length - 1 : 1)) * (width - 2 * padding);
        const y = height - padding - (point.rate / 100) * (height - 2 * padding);
        return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
    }).join(' ');

    return (
        <svg width="100%" viewBox={`0 0 ${width} ${height}`} style={{ backgroundColor: '#f9f9f9', border: '1px solid #ddd', borderRadius: '5px' }}>
            {[0, 25, 50, 75, 100].map(val => {
                const y = height - padding - (val / 100) * (height - 2 * padding);
                return (
                    <g key={val}>
                        <text x={padding - 10} y={y} textAnchor="end" dy="0.3em" fontSize="10" fill="#666">{val}%</text>
                        <line x1={padding} y1={y} x2={width - padding} y2={y} stroke={val === 0 ? "#aaa" : "#e0e0e0"} />
                    </g>
                );
            })}
            <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke="#aaa" />
            {data.length > 1 && <path d={pathData} fill="none" stroke="#007bff" strokeWidth="3" />}
            {data.length > 1 && data.slice(1).map((point, index) => {
                const realIndex = index + 1;
                const x = padding + (realIndex / (data.length - 1)) * (width - 2 * padding);
                const y = height - padding - (point.rate / 100) * (height - 2 * padding);
                let color = '#6c757d';
                if (point.difficulty === 'easy') color = '#28a745';
                if (point.difficulty === 'medium') color = '#ffc107';
                if (point.difficulty === 'hard') color = '#dc3545';
                return (
                    <g key={index}>
                        <circle cx={x} cy={y} r="4" fill={color} />
                        <text x={x} y={height - padding + 15} textAnchor="middle" fontSize="9" fill={color} fontWeight="bold">
                            {point.difficulty.charAt(0).toUpperCase()}
                        </text>
                        <text x={x} y={height - padding + 25} textAnchor="middle" fontSize="9" fill="#666">
                            Q{realIndex}
                        </text>
                    </g>
                );
            })}
        </svg>
    );
};

const Tutorial = () => {
    const { homeworkId } = useParams();
    const [sessionState, setSessionState] = useState(null);
    const [questionHistory, setQuestionHistory] = useState([]);
    const [answersHistory, setAnswersHistory] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [performanceHistory, setPerformanceHistory] = useState([{ rate: 0, difficulty: 'start' }]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const start = async () => {
            try {
                const homework = await homeworkAPI.getById(homeworkId);
                const res = await tutorAPI.startSession(homeworkId, homework.data.lessonPlanId);
                setSessionState(res.data);
                if (res.data.question) {
                    setQuestionHistory([res.data.question]);
                    setAnswersHistory([{ submitted: false, selectedKey: null, isCorrect: false, correctAnswer: '', furtherInformation: '' }]);
                    setPerformanceHistory([{ rate: 0, difficulty: 'start' }]);
                }
            } catch (err) {
                setError(err.response?.data?.error || 'Failed to start tutorial session.');
            } finally {
                setLoading(false);
            }
        };
        start();
    }, [homeworkId]);

    const handleOptionClick = async (optionKey) => {
        if (answersHistory[currentIndex]?.submitted) return;

        const currentQuestion = questionHistory[currentIndex];
        const studentAnswer = currentQuestion.options[optionKey];
        
        try {
            const res = await tutorAPI.submitAnswer({
                studentStateId: sessionState.studentStateId,
                sessionId: sessionState.sessionId,
                studentAnswer: studentAnswer,
                question: currentQuestion,
            });

            const newAnswersHistory = [...answersHistory];
            newAnswersHistory[currentIndex] = {
                submitted: true,
                selectedKey: optionKey,
                isCorrect: res.data.isCorrect,
                correctAnswer: res.data.correctAnswer,
                // +++ STORE THE NEW DATA +++
                furtherInformation: res.data.furtherInformation,
            };
            setAnswersHistory(newAnswersHistory);
            
            setPerformanceHistory(prev => [...prev, {
                rate: res.data.progress.successRate,
                difficulty: currentQuestion.difficulty
            }]);

            if (res.data.nextQuestion && !questionHistory.find(q => q.question === res.data.nextQuestion.question)) {
                setQuestionHistory(prev => [...prev, res.data.nextQuestion]);
                setAnswersHistory(prev => [...prev, { submitted: false, selectedKey: null, isCorrect: false, correctAnswer: '', furtherInformation: '' }]);
            }
        } catch (err) {
            setError('Failed to submit answer.');
        }
    };

    const handleNext = () => {
        if (currentIndex < questionHistory.length - 1) {
            setCurrentIndex(prev => prev + 1);
        }
    };

    const handlePrevious = () => {
        if (currentIndex > 0) {
            setCurrentIndex(prev => prev - 1);
        }
    };

    const getOptionStyle = (optionKey) => {
        const currentAnswerState = answersHistory[currentIndex];
        if (!currentAnswerState?.submitted) return { backgroundColor: '#f0f0f0', color: 'black' };

        const correctOptionValue = currentAnswerState.correctAnswer;
        const thisOptionValue = questionHistory[currentIndex].options[optionKey];

        if (thisOptionValue === correctOptionValue) return { backgroundColor: '#28a745', color: 'white' };
        if (optionKey === currentAnswerState.selectedKey) return { backgroundColor: '#dc3545', color: 'white' };
        return { backgroundColor: '#f0f0f0', color: 'black' };
    };

    if (loading) return <p>Loading tutorial...</p>;
    if (error) return <p style={{ color: 'red' }}>{error}</p>;

    const currentQuestion = questionHistory[currentIndex];
    const currentAnswerState = answersHistory[currentIndex];
    const currentPerformance = performanceHistory[performanceHistory.length - 1].rate;

    return (
        <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto', fontFamily: 'sans-serif' }}>
            <Link to={`/homework/${homeworkId}`}>← Exit Tutorial</Link>
            <h1>AI Tutor: Activation</h1>
            <hr />

            {currentQuestion ? (
                <div>
                    {/* +++ FIX: Render Question with Markdown +++ */}
                    <div style={{ fontSize: '1.2em', marginTop: '20px' }}>
                        <ReactMarkdown components={{ p: 'p' }}>{currentQuestion.question}</ReactMarkdown>
                    </div>
                    
                    <div style={{ margin: '20px 0' }}>
                        {Object.entries(currentQuestion.options).map(([key, value]) => (
                            <button 
                                key={key} 
                                onClick={() => handleOptionClick(key)}
                                style={{
                                    display: 'block', width: '100%', padding: '15px', margin: '10px 0',
                                    border: '1px solid #ddd', borderRadius: '8px', textAlign: 'left',
                                    cursor: currentAnswerState?.submitted ? 'not-allowed' : 'pointer',
                                    ...getOptionStyle(key)
                                }}
                            >
                                {/* +++ FIX: Render Option Value with Markdown +++ */}
                                <strong>{key}.</strong> <span style={{ display: 'inline-block', verticalAlign: 'top' }}><ReactMarkdown components={{ p: 'span' }}>{value}</ReactMarkdown></span>
                            </button>
                        ))}
                    </div>

                    {/* +++ UPDATED FEEDBACK SECTION +++ */}
                    {currentAnswerState?.submitted && (
                        <div style={{ padding: '15px', borderRadius: '8px', color: currentAnswerState.isCorrect ? '#155724' : '#721c24', backgroundColor: currentAnswerState.isCorrect ? '#d4edda' : '#f8d7da' }}>
                            <p style={{ margin: 0, fontWeight: 'bold' }}>{currentAnswerState.isCorrect ? "That's right!" : "Not quite."}</p>
                            {!currentAnswerState.isCorrect && (
                                <div style={{ margin: '5px 0 0 0' }}>
                                    The correct answer was: <strong><ReactMarkdown components={{ p: 'span' }}>{currentAnswerState.correctAnswer}</ReactMarkdown></strong>
                                </div>
                            )}
                            {/* Display the new information */}
                            <div style={{ margin: '10px 0 0 0', borderTop: '1px solid #cce5ff', paddingTop: '10px' }}>
                                <ReactMarkdown components={{ p: 'span' }}>{currentAnswerState.furtherInformation}</ReactMarkdown>
                            </div>
                        </div>
                    )}

                </div>
            ) : (
                <p>All questions for this phase have been completed!</p>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '30px' }}>
                <button onClick={handlePrevious} disabled={currentIndex === 0}>Previous</button>
                <button onClick={handleNext} disabled={!currentAnswerState?.submitted || currentIndex === questionHistory.length - 1}>Next</button>
            </div>
            
            <div style={{ marginTop: '40px', borderTop: '1px solid #ccc', paddingTop: '20px' }}>
                <h1>Teacher Zone</h1>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h2 style={{ fontSize: '1.2em', margin: '0 0 10px 0', fontWeight: 'normal' }}>Performance Tracker</h2>
                    <p style={{ margin: '0 0 10px 0', fontSize: '1.1em', fontWeight: 'bold' }}>Current: {currentPerformance}%</p>
                </div>
                <LineChart data={performanceHistory} />
            </div>
        </div>
    );
};

export default Tutorial;